import { UserRole } from '../types';
import type { Notification } from '../services/notificationService';
import { formatPlateNumber } from './plateNumber';
import { getStatusSlug } from './statusLabel';

/** The i18next `t`. Typed loosely on purpose: i18next's own declarations use TypeScript 5
 *  syntax that this project's TypeScript 4.9 cannot read, which turns `TFunction` into a
 *  zero-argument function. */
type Translate = (...args: any[]) => any;

/**
 * How a notification reads on screen — shared by the desktop bell dropdown and the phone
 * notifications sheet so the two can never word the same event differently.
 */

/** "just now", "5 min", "3 h", "2 d", then the date. */
export function notificationTimeAgo(dateString: string, t: Translate): string {
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const fill = (key: string, count: number) => {
    const result = String(t(key, { count }));
    // Some locales keep the literal {count}; interpolate it ourselves.
    return result.includes('{count}') ? result.replace('{count}', String(count)) : result;
  };
  if (diffMins < 1) return String(t('notifications.justNow'));
  if (diffMins < 60) return fill('notifications.minutesAgo', diffMins);
  if (diffHours < 24) return fill('notifications.hoursAgo', diffHours);
  if (diffDays < 7) return fill('notifications.daysAgo', diffDays);
  return date.toLocaleDateString();
}

/** Workshops and agents know an order by its car; everyone else by its number. */
export function notificationOrderLabel(n: Notification, role: UserRole | undefined): string {
  if (role === UserRole.AGENT || role === UserRole.WORKSHOP) {
    const vehicle = n.order.vehicle;
    if (vehicle) {
      const modelName = vehicle.model?.name || '';
      const year = vehicle.year || '';
      const plate = vehicle.carPlateNumber || '';
      if (modelName && year) {
        return plate ? `${modelName} ${year} - ${formatPlateNumber(plate)}` : `${modelName} ${year}`;
      }
    }
  }
  return n.order.orderNumber;
}

/** Status messages the switch below has no sentence of its own for (backend statusHistoryService). */
const STATUS_OF_MESSAGE: Record<string, string> = {
  'Order is ready for pickup': 'READY_FOR_PICKUP',
  'Invoice has been requested': 'INVOICE_REQUEST',
  'Order has been invoiced': 'INVOICED',
};

/** The stored English template, put into the reader's language. */
export function notificationMessage(n: Notification, t: Translate): string {
  const message = n.messageTemplate;

  const newBid = message.match(/^New bid submitted by (.+?) for order (.+)$/);
  if (newBid) {
    const supplierName = newBid[1];
    return String(t('notifications.newBidSubmitted', { supplierName, defaultValue: `New bid submitted by ${supplierName}` }));
  }

  if (/^Your bid for order (.+?) has been accepted$/.test(message)) {
    return String(t('notifications.bidAccepted', { defaultValue: 'Your bid has been accepted' }));
  }

  const orderStatus = message.match(/^Order (.+?): (.+)$/);
  if (orderStatus) {
    const statusMessage = orderStatus[2];
    switch (statusMessage) {
      case 'Order is now pending':
        return String(t('notifications.orderPending', { defaultValue: 'Order is now pending' }));
      case 'Order has been quoted':
        return String(t('notifications.orderQuoted', { defaultValue: 'Order has been quoted' }));
      case 'Payment has been confirmed (PR)':
        return String(t('notifications.orderPR', { defaultValue: 'Payment has been confirmed (PR)' }));
      case 'Order has been transferred to supplier':
        return String(t('notifications.orderTransferred', { defaultValue: 'Order has been transferred to supplier' }));
      case 'Order has been delivered':
        return String(t('notifications.orderDelivered', { defaultValue: 'Order has been delivered' }));
      case 'Order has been canceled':
        return String(t('notifications.orderCanceled', { defaultValue: 'Order has been canceled' }));
      default: {
        // The status the message names (the order may have moved on since); the status keys are
        // camelCase («readyForPickup»), so the old lower-cased lookup always fell back to English.
        const status = STATUS_OF_MESSAGE[statusMessage] || n.order.status;
        const statusTranslation = String(t(`status.${getStatusSlug(status)}`, { defaultValue: statusMessage }));
        return String(t('notifications.orderStatusChanged', {
          newStatus: statusTranslation,
          defaultValue: `Order status changed to ${statusTranslation}`,
        }));
      }
    }
  }

  return message;
}
